import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useTableColumnPolicy,
  type TableColumnPolicy,
} from '@/api/hooks/useTableColumnPolicies';
import { columnIdOf } from './column-prefs';

/**
 * Column visibility levels (regular vs admin-only) for every WmsTable.
 *
 * A column is admin-only when the DB policy says so for its tableKey+columnId
 * (`true`/`false` override), or — absent a policy entry — when its column def
 * carries `meta.adminOnly: true` (code-level default). Non-admins never see
 * admin-only columns: WmsTable drops them before the column model is built,
 * so the column manager, CSV export, persisted prefs, and mobile cards all
 * agree.
 *
 * The context DEFAULT is `null` → no gating. Only the app shell mounts the
 * provider (MainLayout); provider-less mounts (unit tests) keep today's
 * behavior with zero changes. This is a UI-level declutter feature, NOT a
 * security boundary — the underlying fields may still be in API payloads.
 */
export interface ColumnPolicyContextValue {
  isAdmin: boolean;
  /** undefined while loading / on fetch error → gate by code defaults only. */
  policy: TableColumnPolicy | undefined;
}

// Exported for tests (wrap WmsTable in a raw Provider value without needing
// the QueryClient/auth-store plumbing ColumnPolicyProvider pulls in).
export const ColumnPolicyContext =
  createContext<ColumnPolicyContextValue | null>(null);

/** Mounted once in MainLayout. */
export function ColumnPolicyProvider({ children }: { children: ReactNode }) {
  const { isAdmin } = usePermissions();
  const { data: policy } = useTableColumnPolicy();
  const value = useMemo(() => ({ isAdmin, policy }), [isAdmin, policy]);
  return (
    <ColumnPolicyContext.Provider value={value}>
      {children}
    </ColumnPolicyContext.Provider>
  );
}

export function useColumnPolicyContext(): ColumnPolicyContextValue | null {
  return useContext(ColumnPolicyContext);
}

/** DB override first, then the column def's code-level default. */
export function isColumnAdminOnly(
  col: ColumnDef<any, any>,
  tableKey: string,
  policy: TableColumnPolicy | undefined,
): boolean {
  const id = columnIdOf(col);
  const override = id != null ? policy?.[tableKey]?.[id] : undefined;
  return override ?? col.meta?.adminOnly ?? false;
}

/**
 * The gating filter WmsTable applies to its raw column defs. `ctx == null`
 * (no provider — tests) and admins both pass everything through untouched.
 */
export function filterColumnsByPolicy<TData>(
  columns: ColumnDef<TData, any>[],
  tableKey: string,
  ctx: ColumnPolicyContextValue | null,
): ColumnDef<TData, any>[] {
  if (!ctx || ctx.isAdmin) return columns;
  return columns.filter((col) => !isColumnAdminOnly(col, tableKey, ctx.policy));
}
